import { useEffect, useState } from 'react';
import { supabase } from './supabase';
import { X, Plus, Trash2, Tag, Check, Power } from 'lucide-react';
import './Promotions.css';

// ABM de promociones de catálogo (definidas a mano). TN NO expone las promos
// automáticas por API, así que la promo vigente se carga acá y NewOrder la aplica
// al precio de línea. El monto descontado se guarda en orders.promo_discount (BI).

interface Promo {
    id: number;
    name: string;
    discount_percent: number;
    scope: string;            // 'all' | 'category' | 'product'
    category: string | null;
    product_pattern: string | null;  // substrings separados por | contra el nombre (ej "infinite")
    start_date: string | null;
    end_date: string | null;
    active: boolean;
    created_at?: string;
}

type Props = { onClose: () => void };

const emptyDraft = {
    name: '',
    discount_percent: 10,
    scope: 'all' as 'all' | 'category' | 'product',
    category: '' as string,
    product_pattern: '' as string,
    start_date: '',
    end_date: '',
};

// Categorías de tn_products (para promos por categoría).
const CATEGORIES = ['Libros', 'Iluminación', 'Estantes / Repisas', 'Relojes', 'Floreros', 'Mesas', 'Muebles', 'Decoración', 'Varios'];

function isVigente(p: Promo): boolean {
    if (!p.active) return false;
    const now = Date.now();
    if (p.start_date && new Date(p.start_date).getTime() > now) return false;
    if (p.end_date && new Date(p.end_date).getTime() < now) return false;
    return true;
}

export function Promotions({ onClose }: Props) {
    const [promos, setPromos] = useState<Promo[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [draft, setDraft] = useState(emptyDraft);
    const [saving, setSaving] = useState(false);

    async function load() {
        setLoading(true);
        const { data } = await supabase.from('promotions').select('*').order('created_at', { ascending: false });
        setPromos(data || []);
        setLoading(false);
    }
    useEffect(() => { load(); }, []);

    async function save() {
        if (!draft.name.trim()) { alert('Poné un nombre a la promo'); return; }
        if (draft.scope === 'category' && !draft.category) { alert('Elegí una categoría'); return; }
        if (draft.scope === 'product' && !draft.product_pattern.trim()) { alert('Poné el texto del nombre del producto (ej: infinite)'); return; }
        setSaving(true);
        const row = {
            name: draft.name.trim(),
            discount_percent: Number(draft.discount_percent) || 0,
            scope: draft.scope,
            category: draft.scope === 'category' ? draft.category : null,
            product_pattern: draft.scope === 'product' ? draft.product_pattern.trim().toLowerCase() : null,
            start_date: draft.start_date ? new Date(draft.start_date).toISOString() : null,
            end_date: draft.end_date ? new Date(draft.end_date + 'T23:59:59').toISOString() : null,
            active: true,
        };
        const { error } = await supabase.from('promotions').insert(row);
        setSaving(false);
        if (error) { alert('Error al guardar: ' + error.message); return; }
        setShowForm(false);
        setDraft(emptyDraft);
        load();
    }

    async function toggleActive(p: Promo) {
        await supabase.from('promotions').update({ active: !p.active }).eq('id', p.id);
        load();
    }

    async function remove(p: Promo) {
        if (!window.confirm(`¿Borrar la promo "${p.name}"?`)) return;
        await supabase.from('promotions').delete().eq('id', p.id);
        load();
    }

    const fmtDate = (s: string | null) => s ? new Date(s).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';

    return (
        <div className="promo-overlay" onClick={onClose}>
            <div className="promo-modal" onClick={e => e.stopPropagation()}>
                <div className="promo-modal-head">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Tag size={20} />
                        <h2>Promociones de catálogo</h2>
                    </div>
                    <button className="icon-btn" onClick={onClose}><X size={20} /></button>
                </div>

                <p className="promo-help">
                    Definí acá las promos que corren en Tiendanube (TN no las expone por API). Se aplican
                    automáticamente al crear órdenes/presupuestos manuales, para que coincidan con la web.
                    Las ventas online ya traen la promo aplicada.
                </p>

                {!showForm && (
                    <button className="btn-primary" style={{ alignSelf: 'flex-start', marginBottom: 12 }} onClick={() => setShowForm(true)}>
                        <Plus size={16} /> Nueva promo
                    </button>
                )}

                {showForm && (
                    <div className="promo-form">
                        <div className="promo-form-row">
                            <label>Nombre
                                <input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} placeholder="Ej: 10% invierno" />
                            </label>
                            <label>Descuento %
                                <input type="number" min="0" max="100" value={draft.discount_percent}
                                    onChange={e => setDraft({ ...draft, discount_percent: parseFloat(e.target.value) || 0 })} />
                            </label>
                        </div>
                        <div className="promo-form-row">
                            <label>Alcance
                                <select value={draft.scope} onChange={e => setDraft({ ...draft, scope: e.target.value as 'all' | 'category' | 'product' })}>
                                    <option value="all">Todo el catálogo</option>
                                    <option value="category">Una categoría</option>
                                    <option value="product">Productos por nombre</option>
                                </select>
                            </label>
                            {draft.scope === 'product' && (
                                <label>Nombre contiene
                                    <input value={draft.product_pattern} placeholder='ej: infinite (varios: "infinite|kosmo")'
                                        onChange={e => setDraft({ ...draft, product_pattern: e.target.value })} />
                                </label>
                            )}
                            {draft.scope === 'category' && (
                                <label>Categoría
                                    <select value={draft.category} onChange={e => setDraft({ ...draft, category: e.target.value })}>
                                        <option value="">Elegí…</option>
                                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </label>
                            )}
                        </div>
                        <div className="promo-form-row">
                            <label>Desde (opcional)
                                <input type="date" value={draft.start_date} onChange={e => setDraft({ ...draft, start_date: e.target.value })} />
                            </label>
                            <label>Hasta (opcional)
                                <input type="date" value={draft.end_date} onChange={e => setDraft({ ...draft, end_date: e.target.value })} />
                            </label>
                        </div>
                        <div className="promo-form-actions">
                            <button className="btn-secondary" onClick={() => { setShowForm(false); setDraft(emptyDraft); }}>Cancelar</button>
                            <button className="btn-primary" disabled={saving} onClick={save}>
                                <Check size={16} /> {saving ? 'Guardando…' : 'Guardar'}
                            </button>
                        </div>
                    </div>
                )}

                <div className="promo-list">
                    {loading ? <p className="promo-empty">Cargando…</p>
                        : promos.length === 0 ? <p className="promo-empty">No hay promos cargadas.</p>
                            : promos.map(p => {
                                const vig = isVigente(p);
                                return (
                                    <div key={p.id} className={`promo-item ${vig ? 'vigente' : ''}`}>
                                        <div className="promo-item-main">
                                            <span className="promo-badge">-{p.discount_percent}%</span>
                                            <div className="promo-item-info">
                                                <strong>{p.name}</strong>
                                                <span className="promo-item-sub">
                                                    {p.scope === 'all' ? 'Todo el catálogo' : p.scope === 'product' ? `Nombre contiene: "${p.product_pattern}"` : p.category}
                                                    {' · '}{fmtDate(p.start_date)} → {fmtDate(p.end_date)}
                                                    {vig ? <span className="promo-live"> · vigente</span> : <span className="promo-off"> · inactiva</span>}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="promo-item-actions">
                                            <button className="icon-btn" title={p.active ? 'Desactivar' : 'Activar'} onClick={() => toggleActive(p)}>
                                                <Power size={16} style={{ color: p.active ? '#16a34a' : '#94a3b8' }} />
                                            </button>
                                            <button className="icon-btn danger" title="Borrar" onClick={() => remove(p)}><Trash2 size={16} /></button>
                                        </div>
                                    </div>
                                );
                            })}
                </div>
            </div>
        </div>
    );
}
